import { Component } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ArmyBuilderConfig } from '../../../global/config';
import { HttpClientWithInFlightCache } from '../../../global/httpClient';
import { catchError, firstValueFrom, map, Observable, of, switchMap } from 'rxjs';
import { ToastService } from '../../../global/toast/toast';

interface AdminForceItem {
    id: string;
    name: string;
    faction: string;
    pointsCost: number;
    gameId: string;
    deleted: boolean;
    deletedAt: number | null;
    sharedUrl: string;
}

interface AdminForceGroup {
    gameId: string;
    forces: AdminForceItem[];
}

@Component({
    standalone: false,
    selector: 'user-forces-page',
    template: `
        <abs-page-layout pageTitle="User Forces">
            <div class="content">
                <div class="buttons">
                    <ion-button fill="outline" class="btn-sm" [routerLink]="['/admin/users']">Back to User Lookup</ion-button>
                    <ion-button
                        color="danger"
                        fill="outline"
                        class="btn-sm"
                        [disabled]="deletedForceCount === 0"
                        (click)="permanentlyDeleteAllSoftDeletedForces()"
                        >Delete All Soft-Deleted ({{ deletedForceCount }})</ion-button
                    >
                </div>
                <h2>{{ decodedEmail }}</h2>
                <div *ngIf="userForcesError" class="error">{{ userForcesError }}</div>

                <div *ngIf="userForcesGrouped$ | async as forceGroups">
                    <div *ngIf="forceGroups.length === 0">No forces found for this user.</div>

                    <div *ngFor="let group of forceGroups">
                        <ion-item lines="none">
                            <ion-label>
                                <h3>{{ group.gameId }}</h3>
                            </ion-label>
                            <ion-button
                                slot="end"
                                size="small"
                                fill="clear"
                                color="danger"
                                [title]="'Permanently delete all forces in ' + group.gameId"
                                (click)="permanentlyDeleteForGameId(group.gameId, group.forces.length)"
                            >
                                <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
                            </ion-button>
                        </ion-item>
                        <ion-list>
                            <ion-item *ngFor="let force of group.forces" [color]="force.deleted ? 'light' : null">
                                <ion-label>
                                    <h3>
                                        <a [href]="force.sharedUrl" target="_blank" rel="noopener noreferrer">{{ force.name }}</a>
                                        <span *ngIf="force.deleted"> (Deleted)</span>
                                    </h3>
                                    <p>Faction: {{ force.faction }} | Points: {{ force.pointsCost }}</p>
                                    <p *ngIf="force.deleted">
                                        Deleted At:
                                        {{ force.deletedAt ? (force.deletedAt | date: 'medium') : 'Unknown' }}
                                    </p>
                                </ion-label>
                                <ion-button *ngIf="force.deleted" slot="end" class="btn-sm" fill="outline" (click)="restoreForce(force.id)"
                                    >Restore</ion-button
                                >
                                <ion-button
                                    slot="end"
                                    size="small"
                                    color="danger"
                                    fill="clear"
                                    [title]="force.deleted ? 'Permanently delete soft-deleted force' : 'Permanently delete active force'"
                                    (click)="permanentlyDeleteForce(force.id, force.name, force.deleted)"
                                >
                                    <ion-icon name="trash-outline" slot="icon-only"></ion-icon>
                                </ion-button>
                            </ion-item>
                        </ion-list>
                    </div>
                </div>
            </div>
        </abs-page-layout>
    `
})
export class UserForcesPage {
    userForcesUrl = this.appConfig.apiBaseUrl + '/userData/admin/forces/';
    decodedEmail = '';
    userForcesError = '';
    deletedForceCount = 0;
    userForcesGrouped$: Observable<AdminForceGroup[]>;

    constructor(
        private route: ActivatedRoute,
        private httpClient: HttpClientWithInFlightCache,
        protected appConfig: ArmyBuilderConfig,
        private toastService: ToastService
    ) {
        this.userForcesGrouped$ = this.route.paramMap.pipe(
            map((params) => decodeURIComponent(params.get('email') || '')),
            switchMap((email) => {
                this.decodedEmail = email;
                this.userForcesError = '';
                this.deletedForceCount = 0;

                if (!email) {
                    this.userForcesError = 'Missing user email';
                    return of([]);
                }

                return this.fetchUserForces(email);
            })
        );
    }

    async restoreForce(forceId: string) {
        if (!this.decodedEmail || !forceId) {
            return;
        }

        try {
            await firstValueFrom(
                this.httpClient.post(
                    this.userForcesUrl + encodeURIComponent(this.decodedEmail) + '/' + forceId + '/restore',
                    {},
                    {
                        headers: { ...this.appConfig.globalRequestHeaders },
                        withCredentials: true,
                        requiresLogin: true
                    }
                )
            );

            this.toastService.showToast('', 'Force restored', { color: 'success', duration: 2500 });
            this.refreshForces();
        } catch (error: any) {
            this.toastService.showToast('', error?.error?.message || 'Failed to restore force', {
                color: 'danger',
                duration: 3000
            });
        }
    }

    async permanentlyDeleteForce(forceId: string, forceName: string, isSoftDeleted: boolean) {
        if (!this.decodedEmail || !forceId) {
            return;
        }

        const deleteType = isSoftDeleted ? 'soft-deleted' : 'active';
        if (!confirm(`Permanently delete the ${deleteType} force "${forceName || forceId}"? This cannot be undone.`)) {
            return;
        }

        try {
            await firstValueFrom(
                this.httpClient.delete(this.userForcesUrl + encodeURIComponent(this.decodedEmail) + '/' + forceId + '/permanent', null, {
                    headers: { ...this.appConfig.globalRequestHeaders },
                    withCredentials: true,
                    requiresLogin: true
                })
            );

            this.toastService.showToast('', 'Force permanently deleted', { color: 'success', duration: 2500 });
            this.refreshForces();
        } catch (error: any) {
            this.toastService.showToast('', error?.error?.message || 'Failed to permanently delete force', {
                color: 'danger',
                duration: 3000
            });
        }
    }

    async permanentlyDeleteForGameId(gameId: string, forceCount: number) {
        if (!this.decodedEmail || !gameId || forceCount <= 0) {
            return;
        }

        if (
            !confirm(
                `Permanently delete all ${forceCount} force(s) for gameId "${gameId}"? This includes active and soft-deleted forces and cannot be undone.`
            )
        ) {
            return;
        }

        try {
            const response: any = await firstValueFrom(
                this.httpClient.delete(
                    this.userForcesUrl + encodeURIComponent(this.decodedEmail) + '/by-game/' + encodeURIComponent(gameId) + '/permanent',
                    null,
                    {
                        headers: { ...this.appConfig.globalRequestHeaders },
                        withCredentials: true,
                        requiresLogin: true
                    }
                )
            );

            const deletedCount = response?.deletedCount ?? forceCount;
            this.toastService.showToast('', `Permanently deleted ${deletedCount} force(s) for ${gameId}`, {
                color: 'success',
                duration: 3000
            });
            this.refreshForces();
        } catch (error: any) {
            this.toastService.showToast('', error?.error?.message || 'Failed to permanently delete forces for game', {
                color: 'danger',
                duration: 3000
            });
        }
    }

    async permanentlyDeleteAllSoftDeletedForces() {
        if (!this.decodedEmail || this.deletedForceCount === 0) {
            return;
        }

        if (!confirm(`Permanently delete all ${this.deletedForceCount} soft-deleted forces for this user? This cannot be undone.`)) {
            return;
        }

        try {
            const response: any = await firstValueFrom(
                this.httpClient.delete(this.userForcesUrl + encodeURIComponent(this.decodedEmail) + '/permanent', null, {
                    headers: { ...this.appConfig.globalRequestHeaders },
                    withCredentials: true,
                    requiresLogin: true
                })
            );

            const deletedCount = response?.deletedCount ?? this.deletedForceCount;
            this.toastService.showToast('', `Permanently deleted ${deletedCount} force(s)`, {
                color: 'success',
                duration: 3000
            });
            this.refreshForces();
        } catch (error: any) {
            this.toastService.showToast('', error?.error?.message || 'Failed to permanently delete soft-deleted forces', {
                color: 'danger',
                duration: 3000
            });
        }
    }

    private refreshForces() {
        this.userForcesError = '';
        this.userForcesGrouped$ = this.fetchUserForces(this.decodedEmail);
    }

    private fetchUserForces(email: string): Observable<AdminForceGroup[]> {
        return this.httpClient
            .get(this.userForcesUrl + encodeURIComponent(email), {
                headers: { ...this.appConfig.globalRequestHeaders },
                withCredentials: true,
                requiresLogin: true
            })
            .pipe(
                map((forces: any[]) => {
                    const safeForces = forces || [];
                    this.deletedForceCount = safeForces.filter((f) => f?.deleted === true).length;
                    return this.groupForcesByGameId(safeForces);
                }),
                catchError((error) => {
                    this.userForcesError = error?.error?.message || 'Failed to load forces';
                    this.deletedForceCount = 0;
                    return of([]);
                })
            );
    }

    private groupForcesByGameId(forces: any[]): AdminForceGroup[] {
        const origin = typeof window !== 'undefined' ? window.location.origin : '';
        const forceGroups = forces.reduce((agg: Record<string, AdminForceItem[]>, force) => {
            const gameId = force?.gameId || 'unknown';
            if (!agg[gameId]) {
                agg[gameId] = [];
            }

            agg[gameId].push({
                id: force.id,
                name: force.name || 'Unnamed Force',
                faction: force.faction || 'Unknown',
                pointsCost: force.pointsCost ?? 0,
                gameId,
                deleted: force.deleted === true,
                deletedAt: force.deletedAt ?? null,
                sharedUrl: `${origin}/${gameId}/forces/shared/${force.id}`
            });

            return agg;
        }, {});

        return Object.keys(forceGroups)
            .sort()
            .map((gameId) => ({
                gameId,
                forces: forceGroups[gameId].sort((a, b) => a.name.localeCompare(b.name))
            }));
    }
}
